/**
 * Date utilities (ported from `imports/helpers/dateHelpers.js` +
 * `getDateSlashSeparator` from `projectHelpers.js`).
 *
 * Only the moment-free helpers are here. The moment-dependent ones
 * (getDuplicateDate, isOverdue, isDateWasMoreThanMonthAgo, isPeriodsOverlap,
 * dateToTimePeriod's ISO-week/quarter) are deferred pending a date-library
 * decision for project-frontier (it has none yet — do NOT hand-roll ISO weeks).
 * `getCompanyDateFormatQuery` is a Mongo aggregation → belongs in the backend.
 */

/** Parse a "dd/mm/yyyy" string into a Date. */
export function getDateSlashSeparator(value: string): Date {
  const parts = value.split('/');
  return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
}

/** Shift a Date's wall-clock time to UTC by removing the local offset. */
export function dateToUTC(date: Date): Date {
  if (!date?.getTimezoneOffset || !date?.setMinutes || !date?.getMinutes) {
    return date;
  }
  const dateOffset = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() - dateOffset);
  return date;
}

/** Shift a Date (or date string) into the local timezone by adding the offset. */
export function dateToLocalTimeZone(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!dateObj?.getTimezoneOffset || !dateObj?.setMinutes || !dateObj?.getMinutes) {
    return dateObj;
  }
  const dateOffset = dateObj.getTimezoneOffset();
  dateObj.setMinutes(dateObj.getMinutes() + dateOffset);
  return dateObj;
}

/** Apply {@link dateToLocalTimeZone} to every own property of an object. */
export function convertAllObjectDatesToLocalTimezone(
  object: Record<string, unknown>,
): Record<string, unknown> {
  for (const key of Object.keys(object)) {
    object[key] = dateToLocalTimeZone(object[key] as Date | string);
  }
  return object;
}

/** Detect the runtime timezone, falling back to a default when unavailable. */
export function getSystemTimezone(): string {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      return timezone;
    }
  } catch {
    // Intl unavailable — fall through to default
  }
  return 'America/New_York';
}

/**
 * Today as a UTC-midnight calendar date.
 *
 * The shape every date field and every `toISODate` call site uses: those read
 * `getUTC*` / `toISOString().slice(0, 10)`, so a calendar date has to be UTC
 * midnight to round-trip to the day it names.
 *
 * WHICH day is today, though, is a LOCAL question, and the parts are read
 * locally for that reason. `DateInput` used to derive it from `getUTC*`
 * throughout — internally consistent, but it meant that before 10am in Sydney
 * (UTC+10) the picker called YESTERDAY today: the Today button wrote it, the
 * is-today dot marked it, and an empty field opened on it. That is the first
 * hour of every working day for the tenants who reported it.
 *
 * Pair this only with {@link nowTimeString}, never with a UTC-derived clock —
 * a date from one calendar beside a time from another is a moment that exists
 * in neither.
 */
export function todayCalendarDate(now: Date = new Date()): Date {
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

/**
 * The current time of day as `"HH:mm"` — the wire format `TimeInput` reads and
 * writes.
 *
 * Local parts, deliberately: a time of day means the time on the reader's own
 * clock. There is no UTC-midnight calendar date to stay consistent with here,
 * only {@link todayCalendarDate}, which reads its parts the same way.
 */
export function nowTimeString(now: Date = new Date()): string {
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

/**
 * A chosen calendar date + time of day, as the INSTANT they name on the
 * reader's own clock.
 *
 * This is the pairing v1 got for free. Its pickers handed Meteor a `Date`, and
 * Meteor stores a `Date` as a true instant, so `sendPulseCheckAt` was an
 * absolute moment and `pulseCheckSurveyCron.js:344` could compare it to `now`
 * with no timezone anywhere in the system. v1 has no company timezone at all —
 * the only zone it records is per-user, read off the browser at sign-in
 * (`Login.jsx:220`), and the cron never consults it.
 *
 * v2 wrote `` `${date}T${time}:00.000Z` `` instead — the wall clock wearing a
 * literal `Z`, which names a different moment in every zone but UTC. Every
 * scheduled send was then late by the tenant's offset, and reading it back as
 * UTC made it look correct in the form, which is what kept it hidden.
 *
 * `date` is a UTC-midnight calendar date (`toISODate`'s shape) and `time` is
 * `"HH:mm"`; the DAY is taken with `getUTC*` because that is how the calendar
 * date encodes it, and the resulting parts are then read as LOCAL — which is
 * the whole point.
 */
export function toInstantFromLocalParts(date: Date, time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    Number.isFinite(hours) ? hours : 0,
    Number.isFinite(minutes) ? minutes : 0,
    0,
    0,
  ).toISOString();
}

/**
 * The inverse of {@link toInstantFromLocalParts}: the calendar date and time of
 * day an instant names on the reader's clock.
 *
 * Returns the date in `toISODate`'s UTC-midnight shape so it drops straight
 * into a `DateInput`, and the time as `"HH:mm"` for `TimeInput`. Both come from
 * the same local reading, so they cannot disagree about which day it is — the
 * failure `todayCalendarDate` documents.
 */
export function localPartsFromInstant(value: string | Date): {
  date: Date;
  time: string;
} {
  const instant = typeof value === 'string' ? new Date(value) : value;
  return {
    date: new Date(Date.UTC(instant.getFullYear(), instant.getMonth(), instant.getDate())),
    time: nowTimeString(instant),
  };
}

/**
 * Whether a stored ISO value is a CALENDAR DATE rather than an instant.
 *
 * Both are ISO strings, and nothing in the value says which was meant, so this
 * is a heuristic and worth naming as one: a calendar date is written at UTC
 * midnight (`toISODate`'s shape, and what every date-only field stores), while
 * an instant produced by {@link toInstantFromLocalParts} carries the chosen
 * time of day. Exactly-midnight-UTC is therefore read as a calendar date.
 *
 * It is wrong in one case and right in the rest: an instant that lands exactly
 * on UTC midnight — 08:00 in Manila, 11:00 in Sydney — is read as a calendar
 * date. That renders the same day it names for those zones, so the visible
 * result is correct anyway; the cost is a `12:00` default in the time field
 * rather than the `00:00` that was stored.
 *
 * The alternative — treating every `dueDate` as an instant — misreads every
 * LEGACY row (all of which are midnight-UTC) by a day for viewers west of UTC,
 * which is a much larger population of wrong answers.
 */
export function isCalendarOnly(value: string | Date): boolean {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return true;

  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

/**
 * The calendar date a stored value names to the reader, as a UTC-midnight
 * `Date` — the shape `formatDate` and every UTC-reading comparison expect.
 *
 * A calendar date passes through untouched; an instant is resolved on the
 * local clock first. This is what lets a column keep its UTC reads while
 * showing the right DAY for a value that now carries a time of day.
 */
export function displayCalendarDate(value: string | Date): Date {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return date;

  return isCalendarOnly(date)
    ? new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
    : localPartsFromInstant(date).date;
}

/**
 * The calendar date a stored date-only field names, for display and for
 * seeding a date picker (MTFM-646). Three shapes share these fields:
 *
 * - 00:00:00.000Z — a Falcon-written calendar date. Its UTC day.
 * - 23:59:59.999Z (older rows: .000Z) — the backend's end-of-day for a range end (key dates). Its
 *   UTC day too; read locally it lands on the NEXT day east of UTC.
 * - anything else — Meteor's author-local midnight converted to UTC
 *   (`2026-04-27T15:55:10.333Z` = 01:55 on the 28th in Sydney). Meteor shows
 *   it in the viewer's zone, so it is read on the local clock, as
 *   {@link displayCalendarDate} does.
 *
 * Returned as a UTC-midnight `Date`, so `getUTC*` reads the day.
 */
export function storedCalendarDate(value: string | Date): Date {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return date;

  const isUtcEndOfDay =
    date.getUTCHours() === 23 &&
    date.getUTCMinutes() === 59 &&
    date.getUTCSeconds() === 59 &&
    // Older key dates store the end as 23:59:59.000Z (admin AD-8).
    (date.getUTCMilliseconds() === 999 || date.getUTCMilliseconds() === 0);

  return isUtcEndOfDay
    ? new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
    : displayCalendarDate(date);
}

/**
 * A stored date-only value as the `YYYY-MM-DD` a date field is seeded with:
 * the day {@link storedCalendarDate} names, so a form shows the same day the
 * list does. Cutting the string instead reads a Meteor-written value (author's
 * local midnight as UTC) a day early east of UTC, and an untouched save then
 * writes that earlier day back for everyone (XC-03, as MTFM-646 for initiatives).
 */
export function toCalendarDateInput(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = storedCalendarDate(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}
